import { Box } from "@mui/material";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";

import * as THREE from "three";
import { useEffect, useState } from "react";
import { ModelRenderer } from "../ModelRenderer";
import { Model } from "../../models/Model";
import { MLEvents } from "../common/ModelLoader";
import { MREvents } from "../MREvents";

export interface ModelTreeViewProps {
  mr: ModelRenderer;
}

function createTreeItems(obj: THREE.Object3D, onClick: any): JSX.Element[] {
  const items: any[] = [];
  for (const child of obj.children) {
    const treeItem = {
      id: `${child.id}`,
      name: `${child.name || child.uuid || 'noname' }: (${child.type})`,
      children: child.children,
      object: child,
    };
    items.push(treeItem);
    // if (child instanceof THREE.Group) {
    //   treeItem.children = createTreeItems(child, onClick);
    // }
  }
  return items.map((item) => (
    <TreeItem
      key={item.id}
      itemId={item.id}
      onClick={() => onClick(item)}
      label={item.name }
    >
      {item.children && createTreeItems(item.object, onClick)}
    </TreeItem>
  ));

}

export function ModelTreeView({ mr }: ModelTreeViewProps) {
  const [root, setRoot] = useState<THREE.Object3D | null>(null);
  const [selected, setSelected] = useState<THREE.Object3D | null>(null);
  const [selectedPreviousMaterial, setSelectedPreviousMaterial] = useState<THREE.Material | THREE.Material[] | null>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  useEffect(() => {
    if (!mr) {
      return;
    }
    const modelLoaded = (modelObj: THREE.Object3D) => {
      if (modelObj) {
        setRoot(modelObj);
      } else {
        setRoot(null);
      }
    };
    mr.loader.onEvent(MLEvents.ModelLoaded, modelLoaded);
    if (mr.loader.modelObj) {
      modelLoaded(mr.loader.modelObj);
    } else {
      setRoot(null);
    }
    const objectClicked = (object: THREE.Object3D) => {
      let idTree = [object.id.toString()];
      let parent = object.parent;
      while (parent && parent !== root) {
        idTree.unshift(parent.id.toString());
        parent = parent.parent;
      }
      // console.log({ idTree, root });
      setExpandedItems(idTree);
    };
    mr.onEvent(MREvents.ObjectClicked, objectClicked);
    return () => {
      mr.loader.removeListener(MLEvents.ModelLoaded, modelLoaded);
      mr.removeListener(MREvents.ObjectClicked, objectClicked);
    };
  }, [mr]);
  const onItemClick = (item: any) => {
    if (selected) {
      if (selectedPreviousMaterial) {
        const selectedMesh = selected as THREE.Mesh;
        if (selectedMesh.material) {
          if (Array.isArray(selectedMesh.material)) {
            selectedMesh.material.forEach((mat) => {
              if (mat !== selectedPreviousMaterial) {
                mat.dispose();
              }
            });
          } else {
            selectedMesh.material.dispose();
          }
          selectedMesh.material = selectedPreviousMaterial;
        }
      }
      setSelected(null);
    }
    const selectedObject = root!.getObjectById(parseInt(item.id)) as THREE.Object3D;
    if (selectedObject.type === "Mesh") {
      const mesh = selectedObject as THREE.Mesh;
      setSelected(selectedObject);
      if (mesh.material) {
        setSelectedPreviousMaterial(mesh.material);
        mesh.material = new THREE.MeshBasicMaterial({ color: 0xff0000 });
      }
    }
  };
  if (!root) {
    return <div>Loading...</div>;
  }

  return (
    <Box sx={{ width: '100%', height: "400px", overflow: "auto", background: "#2F2F2F" }}>
      <SimpleTreeView
        expandedItems={expandedItems}
        onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
      >
        {createTreeItems(root, onItemClick)}
      </SimpleTreeView>
    </Box>
  );
}